<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { onMounted, onUnmounted, reactive } from 'vue';
import { eventEmitter, type ShowInputDialogPayload } from '@/lib/eventEmitter.ts';

interface ComponentState {
    isOpen: boolean;
    inputValue: string;
    // Payload
    title?: string;
    description?: string;
    label?: string;
    defaultValue?: string;
    submitText?: string;
    cancelText?: string;
    submitCallback?: (value: string) => void;
    cancelCallback?: () => void;
}

const state: ComponentState = reactive({
    isOpen: false,
    inputValue: '',
    // Payload
    title: undefined,
    description: undefined,
    label: undefined,
    defaultValue: undefined,
    submitText: undefined,
    cancelText: undefined,
    submitCallback: undefined,
    cancelCallback: undefined,
});

const openDialog = (payload: ShowInputDialogPayload) => {
    state.title = payload.title;
    state.description = payload.description;
    state.label = payload.label;
    state.defaultValue = payload.defaultValue;
    state.submitText = payload.submitText;
    state.cancelText = payload.cancelText;
    state.submitCallback = payload.submitCallback;
    state.cancelCallback = payload.cancelCallback;

    state.isOpen = true;
    if (payload.defaultValue) {
        state.inputValue = payload.defaultValue;
    }
};

const cancel = () => {
    if (state.cancelCallback) {
        state.cancelCallback();
    }
    state.isOpen = false;
};

const submit = () => {
    if (state.submitCallback) {
        state.submitCallback(state.inputValue);
    }
    state.isOpen = false;
};

onMounted(async () => {
    eventEmitter.addListener('showInputDialog', openDialog);
});

onUnmounted(() => {
    eventEmitter.removeListener('showInputDialog', openDialog);
});
</script>

<template>
    <Dialog v-model:open="state.isOpen">
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{{ state.title ?? 'Input' }}</DialogTitle>
                <DialogDescription v-if="state.description">
                    {{ state.description }}
                </DialogDescription>
            </DialogHeader>
            <Input
                v-model="state.inputValue"
                :default-value="state.defaultValue"
                :placeholder="state.label"
                @keydown.enter.prevent="submit"
                autofocus
            />
            <DialogFooter>
                <Button type="button" variant="outline" @click="cancel">{{
                    state.cancelText ?? 'Cancel'
                }}</Button>
                <Button type="submit" @click="submit" :disabled="!state.inputValue.length">{{
                    state.submitText ?? 'Submit'
                }}</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
